import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle } from '../ui/drawer';
import { useMediaQuery, SCREEN } from '../../hooks/useMediaQuery';
import CreateMenu from './CreateMenu';
import QuickInput, { taskFormPrefill, useQuickParse } from './QuickInput';

/**
 * "New", the same everywhere: the one input (a task or a question for BusyBee) and under
 * it the create tiles (CreateMenu): a task with every field, a chat, a document and, outside
 * a project, a project; in a project its lists and document types too, all filled in with
 * that project. A dialog on desktop (Ctrl+K, the sidebar's "+ New"), a bottom sheet on phones
 * (the "+" in the tab bar). What was typed stays until it is added, so a stray Escape keeps it.
 */
const NewPanel = ({ open, onOpenChange, project = null, sectionId = null, onTask, onList, onDocument, onChat, onProject }) => {
  const { t } = useTranslation();
  const wide = useMediaQuery(SCREEN.md);
  const [text, setText] = useState('');
  const parsed = useQuickParse(text, project);
  const close = () => onOpenChange(false);

  const title = (
    <>
      {t('nav.create.new')}
      {project && (
        <>
          <span className="w-2 h-2 rounded-[3px] flex-shrink-0" style={{ background: project.color || '#3B82F6' }} />
          <span className="truncate text-sm font-normal text-muted-foreground">{project.name}</span>
        </>
      )}
    </>
  );
  const body = (
    <>
      <QuickInput project={project} sectionId={sectionId} variant={wide ? 'dialog' : 'sheet'} value={text} onValueChange={setText}
        autoFocus={wide} onDone={close} />
      <div className="flex flex-col gap-2.5">
        <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('nav.create.or_create')}</p>
        <CreateMenu variant={wide ? 'panel' : 'sheet'} project={project} docTypes={!!project}
          onTask={() => { close(); onTask(taskFormPrefill(parsed, text, project, sectionId)); setText(''); }}
          onList={project ? () => { close(); onList(project.id); } : undefined}
          onDocument={(type) => { close(); onDocument(project?.id || null, type); }}
          onChat={() => { close(); onChat(project); }}
          onProject={project ? undefined : () => { close(); onProject(); }} />
      </div>
    </>
  );

  if (wide) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[560px] top-[12vh] translate-y-0 p-4 gap-4 rounded-2xl bg-card" data-testid="new-panel">
          <DialogTitle className="flex items-center gap-2 min-w-0 pr-8 text-[15px] font-semibold">{title}</DialogTitle>
          <DialogDescription className="sr-only">{t('nav.create.placeholder')}</DialogDescription>
          {body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh] bg-card" data-testid="new-panel">
        <div className="flex items-center gap-2 px-4 pt-3">
          <DrawerTitle className="flex-1 min-w-0 flex items-center gap-2 text-[17px] font-semibold">{title}</DrawerTitle>
          <DrawerClose aria-label={t('common.close')}
            className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground">
            <X className="w-[18px] h-[18px]" />
          </DrawerClose>
        </div>
        <DrawerDescription className="sr-only">{t('nav.create.placeholder')}</DrawerDescription>
        <div className="overflow-y-auto px-4 pt-3 flex flex-col gap-5" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
          {body}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default NewPanel;
